import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  formatDateTimeWithMilitarySupport,
  isFeatureEnabledForWarehouse,
  LuxonDateTimeFormats,
  WarehouseFeatureEnum
} from '@nova/core';
import useNotify from '@/composables/useNotify';
import useEventHub from '@/composables/useEventHub';
import bolService from '@satellite/services/bol-service';
import BolStateEnum from '@/enums/BolStateEnum';

export const BOL_ALLOWED_FILE_TYPES = Object.freeze([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/heic',
  'application/pdf'
]);

export const BOL_MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export function getBolState(bolData) {
  if (!bolData) {
    return BolStateEnum.NoDoc;
  }
  if (bolData.signedDoc) {
    return BolStateEnum.Signed;
  }
  if (bolData.originalDoc) {
    return BolStateEnum.WaitingSignature;
  }
  return BolStateEnum.NoDoc;
}

export function getShowUploadButton(bolState) {
  return bolState === BolStateEnum.NoDoc;
}

export function getBolStatusLabel(bolState, t) {
  if (bolState === BolStateEnum.Signed) {
    return t('appointment.dialogs.appointmentDetailsDialog.headers.bolSigned');
  }
  if (bolState === BolStateEnum.WaitingSignature) {
    return t('appointment.dialogs.appointmentDetailsDialog.headers.bolWaitingSignature');
  }
  return null;
}

export function validateBolFile(file) {
  if (!file) {
    return { valid: false, error: 'no-file' };
  }

  if (!BOL_ALLOWED_FILE_TYPES.includes(file.type)) {
    return { valid: false, error: 'invalid-type' };
  }

  if (file.size > BOL_MAX_FILE_SIZE) {
    return { valid: false, error: 'file-too-large' };
  }

  return { valid: true, error: null };
}

function getBolApiErrorMessage(error) {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) {
    return message.find(item => typeof item === 'string') ?? null;
  }
  return typeof message === 'string' ? message : null;
}

export function getBolDeleteErrorCode(error) {
  const status = error?.response?.status;

  if (status === 409) {
    return 'already-signed';
  }

  if (status === 403) {
    const message = getBolApiErrorMessage(error);
    if (message?.includes('does not have BOL settings enabled')) {
      return 'not-enabled';
    }
    return 'access-denied';
  }

  return 'delete-error';
}

export default function useBolDocument(warehouseRef, isMilitaryTimeEnabledRef) {
  const { t, locale } = useI18n();
  const notify = useNotify();
  const eventHub = useEventHub();

  const bolData = ref(null);
  const bolLoading = ref(false);
  const bolUploading = ref(false);
  const bolDeleting = ref(false);

  const showBolSection = computed(() => {
    return (
      warehouseRef.value &&
      isFeatureEnabledForWarehouse(warehouseRef.value, WarehouseFeatureEnum.GateManagement)
    );
  });

  const bolState = computed(() => getBolState(bolData.value));

  const showUploadButton = computed(() => getShowUploadButton(bolState.value));

  const bolStatusLabel = computed(() => getBolStatusLabel(bolState.value, t));

  const formattedSignedDate = computed(() => {
    if (!bolData.value?.signedAt || !warehouseRef.value) {
      return null;
    }
    const timezone = warehouseRef.value.timezone || 'UTC';
    return formatDateTimeWithMilitarySupport(
      bolData.value.signedAt,
      timezone,
      LuxonDateTimeFormats.DateSlashedDashTimestamp12HourTimeAMPM,
      isMilitaryTimeEnabledRef.value,
      LuxonDateTimeFormats.DateSlashedDashTimestamp24HourTime,
      locale.value
    );
  });

  async function fetchBolData(appointmentId) {
    if (!appointmentId) {
      bolData.value = null;
      return;
    }

    bolLoading.value = true;
    bolData.value = null;
    try {
      const data = await bolService.getBolByAppointmentId(appointmentId);
      bolData.value = data;
    } catch (error) {
      if (error?.response?.status === 404) {
        bolData.value = null;
      } else {
        console.error('Error fetching BOL data:', error);
      }
    } finally {
      bolLoading.value = false;
    }
  }

  async function uploadBol(appointmentId, file, onSuccess) {
    const validation = validateBolFile(file);
    if (!validation.valid) {
      notify(t('appointment.dialogs.appointmentDetailsDialog.descriptions.bolHelper'), 'error');
      return { success: false, error: validation.error };
    }

    bolUploading.value = true;
    try {
      await bolService.uploadBol(appointmentId, file);
      notify(
        t('appointment.dialogs.appointmentDetailsDialog.messages.bolUploadSuccess'),
        'success'
      );

      await fetchBolData(appointmentId);
      eventHub.$emit('refresh-appointment', appointmentId);

      if (onSuccess) {
        onSuccess(appointmentId);
      }

      return { success: true, error: null };
    } catch (error) {
      const message =
        error?.response?.status === 403
          ? t('appointment.dialogs.appointmentDetailsDialog.messages.bolAlreadySigned')
          : t('appointment.dialogs.appointmentDetailsDialog.messages.bolUploadError');
      notify(message, 'error');
      return {
        success: false,
        error: error?.response?.status === 403 ? 'already-signed' : 'upload-error'
      };
    } finally {
      bolUploading.value = false;
    }
  }

  async function deleteBol(bolId, appointmentId, onSuccess) {
    if (!bolId || !appointmentId) {
      return { success: false, error: 'missing-ids' };
    }

    bolDeleting.value = true;
    try {
      await bolService.deleteBol(bolId);
      notify(
        t('appointment.dialogs.appointmentDetailsDialog.messages.bolDeleteSuccess'),
        'success'
      );

      await fetchBolData(appointmentId);
      eventHub.$emit('refresh-appointment', appointmentId);

      if (onSuccess) {
        onSuccess(appointmentId);
      }

      return { success: true, error: null };
    } catch (error) {
      const errorCode = getBolDeleteErrorCode(error);
      const message =
        errorCode === 'already-signed'
          ? t('appointment.dialogs.appointmentDetailsDialog.messages.bolAlreadySigned')
          : t('appointment.dialogs.appointmentDetailsDialog.messages.bolDeleteError');
      notify(message, 'error');
      return {
        success: false,
        error: errorCode
      };
    } finally {
      bolDeleting.value = false;
    }
  }

  function clearBolData() {
    bolData.value = null;
  }

  return {
    bolData,
    bolLoading,
    bolUploading,
    bolDeleting,
    showBolSection,
    bolState,
    showUploadButton,
    bolStatusLabel,
    formattedSignedDate,
    fetchBolData,
    uploadBol,
    deleteBol,
    clearBolData
  };
}
